import { useMemo } from "react"
import { type TMultiSelectOption } from "~/ui-rtk/components/ui/controls/MultiSelect"
import useConnect from "~/ui-rtk/components/ui/charts/ChartDataLoader/connect"
import { useMinMaxDateRange } from "~/ui-rtk/hooks/date-range"
import { usePeriod } from "~/ui-rtk/hooks/period"

const CUBE = "company_web_analytics_landing_page_report"
const MEDIUM_KEY = `${CUBE}.session_medium`
const SESSIONS_KEY = `${CUBE}.sessions`

/**
 * Fetches the distinct GA4 session mediums for the landing page report ranked by
 * session volume (highest first), so the searchable filter can surface the most
 * relevant mediums at the top of its dropdown.
 */
export function useSessionMediumOptions() {
  const { dateRange } = useMinMaxDateRange()
  const { granularity } = usePeriod()

  const { dataSource, isLoading } = useConnect({
    chartId: "landing_page_report_medium_options_query",
    granularity,
    dateRange: dateRange || [new Date(), new Date()],
    page: 0,
    itemsPerPage: 10000,
    enablePagination: false,
  })

  const mediumOptions = useMemo(() => {
    if (!dataSource) return []

    // Aggregate sessions per medium (defensive — the cube query already groups
    // by session_medium) then sort by session volume descending.
    const sessionsByMedium = new Map<string, number>()
    dataSource.forEach(row => {
      const medium = row[MEDIUM_KEY]
      if (medium === null || medium === undefined || medium === "") return
      const sessions = Number(row[SESSIONS_KEY] ?? 0)
      const key = String(medium)
      sessionsByMedium.set(key, (sessionsByMedium.get(key) ?? 0) + sessions)
    })

    return Array.from(sessionsByMedium.entries())
      .sort(([, a], [, b]) => b - a)
      .map(([medium]) => ({
        value: medium,
        label: medium,
      })) as TMultiSelectOption[]
  }, [dataSource])

  return {
    mediumOptions,
    isLoading,
  }
}

export default useSessionMediumOptions
